<template>
  <div :class="classes">
    <div :class="[prefixCls + '-wrap']" ref="slider" @click.self="sliderClick">
      <input type="hidden" :name="name" :value="exportValue" />
      <div
        :class="[prefixCls + '-bar']"
        :style="barStyle"
        @click.self="sliderClick"
      ></div>

      <div
        :class="minButtonWarpClass"
        :style="{ left: minPosition + '%' }"
        @touchstart="onPointerDown($event, 'min')"
        @mousedown="onPointerDown($event, 'min')"
        :data-val="this.exportValue[0]"
      >
        <div
          :class="minButtonClasses"
          tabindex="0"
          @keydown.left="onKeyLeft($event, 'min')"
          @keydown.down="onKeyLeft($event, 'min')"
          @keydown.right="onKeyRight($event, 'min')"
          @keydown.up="onKeyRight($event, 'min')"
        ></div>
      </div>

      <div
        v-if="range"
        :class="maxButtonWarpClass"
        :style="{ left: maxPosition + '%' }"
        @touchstart="onPointerDown($event, 'max')"
        @mousedown="onPointerDown($event, 'max')"
        :data-val="this.exportValue[1]"
      >
        <div
          :class="maxButtonClasses"
          tabindex="0"
          @keydown.left="onKeyLeft($event, 'max')"
          @keydown.down="onKeyLeft($event, 'max')"
          @keydown.right="onKeyRight($event, 'max')"
          @keydown.up="onKeyRight($event, 'max')"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
const prefixCls = "k-slider";
import elementResizeDetectorMaker from "element-resize-detector";
import { on, off } from "./dom.js";

export default {
  name: "k-slider",
  props: {
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 100
    },
    step: {
      type: Number,
      default: 1
    },
    range: {
      type: Boolean,
      default: false
    },
    value: {
      type: [Number, Array],
      default: 0
    },
    disabled: {
      type: Boolean,
      default: false
    },
    name: {
      type: String
    },
    button: {
      validator(val) {
        return ["circle", "square"].includes(val);
      },
      default: "circle"
    }
  },
  watch: {
    value(val) {
      val = this.checkLimits(Array.isArray(val) ? val : [val]);
      if (
        !this.dragging &&
        (val[0] !== this.currentValue[0] || val[1] !== this.currentValue[1])
      ) {
        this.currentValue = val;
      }
    },
    exportValue(values) {
      const value = this.range ? values : values[0];
      this.$emit("input", value);
      this.$emit("on-input", value);
    }
  },
  computed: {
    classes() {
      return [
        `${prefixCls}`,
        {
          [`${prefixCls}-range`]: this.range,
          [`${prefixCls}-disabled`]: this.disabled
        }
      ];
    },
    minButtonWarpClass() {
      return [
        `${prefixCls}-button-wrap`,
        {
          [`${prefixCls}-button-wrap-dragging`]: this.pointerDown === "min"
        }
      ];
    },
    maxButtonWarpClass() {
      return [
        `${prefixCls}-button-wrap`,
        {
          [`${prefixCls}-button-wrap-dragging`]: this.pointerDown === "max"
        }
      ];
    },
    minButtonClasses() {
      return [
        `${prefixCls}-button`,
        {
          [`${prefixCls}-button-square`]: this.button === "square",
          [`${prefixCls}-button-dragging`]: this.pointerDown === "min"
        }
      ];
    },
    maxButtonClasses() {
      return [
        `${prefixCls}-button`,
        {
          [`${prefixCls}-button-square`]: this.button === "square",
          [`${prefixCls}-button-dragging`]: this.pointerDown === "max"
        }
      ];
    },
    exportValue() {
      const decimalCases = (String(this.step).split(".")[1] || "").length;
      return this.currentValue.map(nr => Number(nr.toFixed(decimalCases)));
    },
    valueRange() {
      return this.max - this.min;
    },
    firstPosition() {
      return this.currentValue[0];
    },
    secondPosition() {
      return this.currentValue[1];
    },
    minPosition() {
      const val = this.currentValue;
      return ((val[0] - this.min) / this.valueRange) * 100;
    },
    maxPosition() {
      const val = this.currentValue;
      return ((val[1] - this.min) / this.valueRange) * 100;
    },
    barStyle() {
      const style = {
        width: ((this.currentValue[0] - this.min) / this.valueRange) * 100 + "%"
      };
      if (this.range) {
        style.left =
          ((this.currentValue[0] - this.min) / this.valueRange) * 100 + "%";
        style.width =
          ((this.currentValue[1] - this.currentValue[0]) / this.valueRange) *
            100 +
          "%";
      }
      return style;
    }
  },
  data() {
    const val = this.checkLimits(
      Array.isArray(this.value) ? this.value : [this.value]
    );
    return {
      prefixCls: prefixCls,
      currentValue: val,
      dragging: false,
      pointerDown: "",
      startX: 0,
      currentX: 0,
      startPos: 0,
      oldValue: [...val],
      valueIndex: {
        min: 0,
        max: 1
      },
      sliderWidth: 0
    };
  },
  methods: {
    checkLimits([min, max]) {
      min = Math.max(this.min, min);
      min = Math.min(this.max, min);
      max = Math.max(this.min, min, max);
      max = Math.min(this.max, max);
      return [min, max];
    },
    getCurrentValue(event, type) {
      if (this.disabled) {
        return;
      }
      const index = this.valueIndex[type];
      if (typeof index === "undefined") {
        return;
      }
      return this.currentValue[index];
    },
    getPointerX(e) {
      return e.type.indexOf("touch") !== -1 ? e.touches[0].clientX : e.clientX;
    },
    handleDecimal(pos, step) {
      if (step < 1) {
        let sl = step.toString(),
          multiple = 1,
          m;
        try {
          m = sl.split(".")[1].length;
        } catch (e) {
          m = 0;
        }
        multiple = Math.pow(10, m);
        return ((pos * multiple) % (step * multiple)) / multiple;
      } else return pos % step;
    },
    emitChange() {
      const value = this.range ? this.exportValue : this.exportValue[0];
      this.$emit("on-change", value);
    },
    sliderClick(event) {
      if (this.disabled) return;
      const currentX = this.getPointerX(event);
      const sliderOffsetLeft = this.$refs.slider.getBoundingClientRect().left;
      let newPos =
        ((currentX - sliderOffsetLeft) / this.sliderWidth) * this.valueRange +
        this.min;
      let regularNewPos = (newPos / this.valueRange) * 100;
      if (!this.range || regularNewPos <= this.minPosition)
        this.changeButtonPosition(newPos, "min");
      else if (regularNewPos >= this.maxPosition)
        this.changeButtonPosition(newPos, "max");
      else
        this.changeButtonPosition(
          newPos,
          newPos - this.firstPosition <= this.secondPosition - newPos
            ? "min"
            : "max"
        );
    },
    camelCase(name) {
      const SPECIAL_CHARS_REGEXP = /([\:\-\_]+(.))/g;
      const MOZ_HACK_REGEXP = /^moz([A-Z])/;
      return name
        .replace(SPECIAL_CHARS_REGEXP, function(_, separator, letter, offset) {
          return offset ? letter.toUpperCase() : letter;
        })
        .replace(MOZ_HACK_REGEXP, "Moz$1");
    },
    getStyle(element, styleName) {
      if (!element || !styleName) return null;
      styleName = this.camelCase(styleName);
      if (styleName === "float") {
        styleName = "cssFloat";
      }
      try {
        const computed = document.defaultView.getComputedStyle(element, "");
        return element.style[styleName] || computed
          ? computed[styleName]
          : null;
      } catch (e) {
        return element.style[styleName];
      }
    },
    handleSetSliderWidth() {
      this.sliderWidth = parseInt(
        this.getStyle(this.$refs.slider, "width"),
        10
      );
    },
    changeButtonPosition(newPos, forceType) {
      const type = forceType || this.pointerDown;
      const index = type === "min" ? 0 : 1;
      if (type === "min") newPos = this.checkLimits([newPos, this.max])[0];
      else newPos = this.checkLimits([this.min, newPos])[1];
      const modulus = this.handleDecimal(newPos, this.step);
      const value = this.currentValue;
      value[index] = newPos - modulus;
      // 判断左右是否相等，否则会出现左边大于右边的情况
      if (this.range) {
        if (type === "min" && value[0] > value[1]) value[1] = value[0];
        if (type === "max" && value[0] > value[1]) value[0] = value[1];
      }
      this.currentValue = [...value];
      if (!this.dragging) {
        if (this.currentValue[index] !== this.oldValue[index]) {
          this.emitChange();
          this.oldValue[index] = this.currentValue[index];
        }
      }
    },
    onKeyLeft(event, type) {
      const value = this.getCurrentValue(event, type);
      if (Number.isFinite(value)) {
        this.changeButtonPosition(value - this.step, type);
      }
    },
    onKeyRight(event, type) {
      const value = this.getCurrentValue(event, type);
      if (Number.isFinite(value)) {
        this.changeButtonPosition(value + this.step, type);
      }
    },
    onPointerDown(event, type) {
      if (this.disabled) return;
      event.preventDefault();
      this.pointerDown = type;
      this.onPointerDragStart(event);
      on(window, "mousemove", this.onPointerDrag);
      on(window, "touchmove", this.onPointerDrag);
      on(window, "mouseup", this.onPointerDragEnd);
      on(window, "touchend", this.onPointerDragEnd);
    },
    onPointerDragStart(event) {
      this.dragging = false;

      this.startX = this.getPointerX(event);

      this.startPos =
        (this[`${this.pointerDown}Position`] * this.valueRange) / 100 +
        this.min;
    },
    onPointerDrag(event) {
      this.dragging = true;

      this.currentX = this.getPointerX(event);

      const diff =
        ((this.currentX - this.startX) / this.sliderWidth) * this.valueRange;

      this.changeButtonPosition(this.startPos + diff);
    },
    onPointerDragEnd() {
      if (this.dragging) {
        this.dragging = false;

        this.emitChange();
      }
      this.pointerDown = "";
      off(window, "mousemove", this.onPointerDrag);
      off(window, "touchmove", this.onPointerDrag);
      off(window, "mouseup", this.onPointerDragEnd);
      off(window, "touchend", this.onPointerDragEnd);
    }
  },
  mounted() {
    this.observer = elementResizeDetectorMaker();
    this.observer.listenTo(this.$refs.slider, this.handleSetSliderWidth);
  },
  beforeDestroy() {
    this.observer.removeListener(this.$refs.slider, this.handleSetSliderWidth);
  }
};
</script>
<style lang="less">
@slider-prefix-cls: k-slider;

//
@primary-color : #2d8cf0;
@cursor-disabled : not-allowed;

// Slider
@slider-color : red;
@slider-height : 6px;
@slider-margin : 16px 0;
@slider-button-wrap-size : 24px;
@slider-button-wrap-offset : -9px;
@slider-disabled-color : #ccc;

// Border color
@border-color-base : #dcdee2; // outside
@border-color-split : #e8eaec; // inside
@border-width-base : 1px; // width of the border for a component
@border-style-base : solid; // style of a components border

// Animation
@animation-time : .3s;
@transition-time : .2s;
@ease-in-out : ease-in-out;

//
@btn-border-radius-small: 3px;

//
.size(@width; @height) {
  width: @width;
  height: @height;
}

.square(@size) {
  .size(@size; @size);
}

.@{slider-prefix-cls} {
  line-height: normal;
  box-shadow: inset 0px 2px 0 #bfbfbf;

  background-color: rgba(204, 204, 204, 0.48);
  padding: 5px;
  border-radius: 50px;
  margin: @slider-margin;

  &-wrap {
    width: 100%;
    height: @slider-height;
    box-shadow: 0 0px 3px #a5a5a5;
    background-color: #efefef;
    border-radius: @btn-border-radius-small;
    vertical-align: middle;
    position: relative;
    cursor: pointer;
  }

  &-button-wrap {
    .square(@slider-button-wrap-size);
    text-align: center;
    background-color: transparent;
    position: absolute;
    top: @slider-button-wrap-offset;
    transform: translateX(-50%);

    &::after {
      position: absolute;
      left: 50%;
      transform: translate(-50%);
      top: -10px; //-10px;
      border-radius: 5px;
      height: 20px;
      line-height: 20px;
      padding: 0 6px;
      background-color: rgba(#000, 0.5);
      opacity: 0;
      color: #fff;
      text-align: center;
      font-size: 12px;
      content: attr(data-val);
      transition: opacity 300ms, top 300ms;
    }

    &::before {
      content: "";
      position: absolute;
      width: 0;
      height: 0;
      left: 50%;
      transform: translate(-50%);
      top: 10px;
      opacity: 0;
      border-width: 5px;
      border-style: solid;
      z-index: 999;
      border-color: rgba(#000, 0.5) transparent transparent transparent;
      transition: opacity 300ms, top 300ms;
    }

    &:hover,
    &-dragging {
      &::after {
        top: -30px;
        opacity: 1;
      }
      &::before {
        top: -10px;
        opacity: 1;
      }
    }
  }

  &-button {
    width: 22px;
    height: 22px;
    border: 1px solid #bfbfbf;
    border-radius: 50%;
    box-shadow: 0 1px 3px 0px #ccc;
    background: linear-gradient(0deg, #ebebeb 20%, #fff 100%);
    transition: all @transition-time linear;
    outline: 0;
    position: relative;

    &::after {
      content: "";
      position: absolute;
      width: 10px;
      height: 10px;
      background-color: #adadad;
      border-radius: 50%;
      left: 5px;
      top: 5px;
      box-shadow: inset 0 2px 2px 1px #636363;
    }

    &-square {
      border-radius: 3px;
      &::after {
        width: 4px;
        height: 8px;
        top: 6px;
        background-color: transparent;
        border-radius: 0;
        border-left: 1px solid #ccc;
        border-right: 1px solid #ccc;
        box-shadow: unset;
      }
      &::before {
        content: "";
        position: absolute;
        height: 8px;
        width: 4px;
        left: 11px;
        top: 6px;
        border-left: 1px solid #ccc;
        border-right: 1px solid #ccc;
      }
    }

    &:focus,
    &:hover,
    &-dragging {
      // border-color: @primary-color;
      transform: scale(1.2);
    }

    &:hover {
      cursor: grab;
    }
    &-dragging,
    &-dragging:hover {
      cursor: grabbing;
    }
  }

  &-bar {
    height: @slider-height;
    background-image: repeating-linear-gradient(
      -45deg,
      #f5f5f5,
      #f5f5f5 3px,
      #ccc 4px
    );
    background-size: 6px;
    border-radius: @btn-border-radius-small;
    position: absolute;
  }

  &-stop {
    position: absolute;
    .square(@slider-height);
    border-radius: 50%;
    background-color: @slider-disabled-color;
    transform: translateX(-50%);
  }
}

.@{slider-prefix-cls}-disabled {
  & > * {
    pointer-events: none;
  }
  cursor: @cursor-disabled;

  opacity: 0.6;
}

.@{slider-prefix-cls}-input {
  .@{slider-prefix-cls}-wrap {
    width: auto;
    margin-right: 100px;
  }
}
</style>
